import { getProductDetailParam, ProductDetail } from '@dh-io-emh/commerce-api/src/models/product';
import { FootnoteItem } from '@dh-io-emh/footnotes/dist';
import {
  BreakdownType,
  Environment,
  FootnoteReferenceGroup,
  PriceDisplayEventData,
  StockProduct,
  SupportedMarket,
} from '@dh-io-pls/price-display';
import { computed, onUnmounted, Ref, ref, unref, watch } from 'vue';
import { infuse } from '../ioc-config';
import { PriceType } from '../types/price.type';
import { isFootnotePilot } from '../utils/footnote.util';
import { Logger } from '../utils/logger.util';
import { useOWCPriceDisplayFootnotes } from '../utils/price-display.util';
import { useFootnoteStore } from './footnotes.composable';
import Vehicle from '../models/vehicle.model';
import { DCPBadgeDTO } from '@dh-io-emh/commerce-api/src/models/shared/ocb';

export enum ProductApiEnum {
  CONFIGURATION = 'CONFIGURATION',
  STOCK = 'STOCK',
}

type FootnoteGroup = {
  type: BreakdownType;
  items: FootnoteItem[];
};

/**
 * A helper class to manage a footnote logic in a different place
 * to divide it from the price display element logic
 */
class FootnoteHelper {
  /**
   * Combine provided footnote groups into unique groups
   */
  public static combineFootnoteGroups = (groups: FootnoteGroup[]) => {
    const combinedFootnoteGroups: FootnoteGroup[] = [];

    groups.forEach((footnoteGroup) => {
      // Create copy of the footnoteGroup object to prevent unwanted mutation of the reference
      const footnoteGroupCopy = {
        type: footnoteGroup.type,
        items: [...footnoteGroup.items],
      };

      // Check if a group with the same type already exists
      const existingFootnoteGroup: FootnoteGroup | undefined = combinedFootnoteGroups.find(
        (combinedFootnoteGroup) => footnoteGroupCopy.type === combinedFootnoteGroup.type,
      );

      // If it already exists push footnote items from the current iteration to the footnoteGroup.items array;
      // and if it doesn't -> push a whole group to the combinedFootnoteGroups array
      if (existingFootnoteGroup) {
        footnoteGroupCopy.items.forEach((item) => {
          // Check if an item with the same text doesn't yet exist
          // and push it to the existingFootnoteGroup.items array
          if (!existingFootnoteGroup.items.some((existingItem) => existingItem.text === item.text)) {
            existingFootnoteGroup.items.push(item);
          }
        });
      } else {
        combinedFootnoteGroups.push(footnoteGroupCopy);
      }
    });

    return combinedFootnoteGroups;
  };

  /**
   * Get updated footnote groups from the footnoteItems array and send an update status back as well
   */
  public static updateFootnoteReferences = (
    footnoteGroups: FootnoteGroup[] = [],
    footnoteItems: FootnoteItem[] = [],
  ): { wasUpdated: boolean; updatedFootnoteGroups: FootnoteGroup[] } => {
    let wasUpdated = false;

    // Map footnoteGroups to the updated footnote references
    const updatedFootnoteGroups = footnoteGroups.map((ppdwcFootnote) => ({
      type: ppdwcFootnote.type,
      items: ppdwcFootnote.items
        .map((item) => {
          // Find a footnote item with the same text in the footnoteItems array
          const footnote = footnoteItems.find((footnote) => footnote.text === item.text) as FootnoteItem;

          // Exit and return undefined when the footnote could not be found in the global footnotes
          if (!footnote) return;

          // If a reference number was changed we have to send it back to proceed with an update
          if (footnote.referenceNumber !== item.referenceNumber) {
            wasUpdated = true;
          }

          return footnote;
        })
        // Filter out the undefined items
        .filter((item) => item),
    }));

    return { wasUpdated, updatedFootnoteGroups };
  };
}

/**
 * A price display composable
 */
function useEMHPriceDisplay(elementRef: Ref<(HTMLElement & Record<string, unknown>) | undefined>) {
  const { addFootnote, addFootnotes, removeFootnotes, footnotes } = useFootnoteStore();

  const footnoteGroups = {
    // PPDWC price specific footnotes (purchase price, strike price, etc.)
    ppdwc: ref<FootnoteGroup[]>([]),
    // EMH price specific footnotes (product-tile image, etc.)
    emh: ref<FootnoteGroup[]>([]),
    // PPDWC and EMH footnotes combined
    combined: computed(() =>
      FootnoteHelper.combineFootnoteGroups([
        ...(footnoteGroups.ppdwc?.value || []),
        ...(footnoteGroups.emh?.value || []),
      ]),
    ),
  };

  /**
   * Handle PPDWC specific footnotes (purchase price, strike price, etc.)
   */
  const handlePpdwcFootnotes = async ({ detail }: CustomEvent<PriceDisplayEventData>) => {
    // 1. Remove all previously registered PPDWC footnotes,
    // because we don't know if some footnotes were added or removed in this event
    await removePriceDisplayFootnotes(footnoteGroups.ppdwc.value);

    // 2. Remove possible duplicate footnotes
    const footnotesWithoutDuplicates =
      detail.footnotes?.map((footnote) => ({
        type: footnote.type,
        items: [...new Set(footnote.items)],
      })) ?? [];

    // 3. Add all footnotes to the store
    const newFootnotes = await addFootnotes(
      footnotesWithoutDuplicates.map((footnoteGroup) => footnoteGroup.items).flat(),
    );

    // 4. Assign PPDWC footnotes to the footnoteGroups.ppdwc array
    // and map recently added footnotes to the FootnoteItem format
    footnoteGroups.ppdwc.value = footnotesWithoutDuplicates.map((footnote) => ({
      type: footnote.type,
      items: footnote.items.map((item) => newFootnotes.find((footnote) => footnote.text === item) as FootnoteItem),
    }));

    // 5. Assign footnotes to the price display element
    assignFootnotesToEl();
  };

  /**
   * Add EMH specific footnote (product-tile image, etc.)
   */
  const addEmhFootnote = async (type: BreakdownType, item: string) => {
    // 1. Add a footnote to the store
    const footnote: FootnoteItem = await addFootnote(item);

    // 2. Add recently added footnote to the footnoteGroups.emh array
    footnoteGroups.emh.value = [...footnoteGroups.emh.value, { type, items: [footnote] }];

    // 3. Assign footnotes to the price display element
    assignFootnotesToEl();
  };

  /**
   * Sort footnote imtems and assign them to the footnoteReferences array of the price display element
   */
  const assignFootnotesToEl = () => {
    if (!elementRef.value) return;

    elementRef.value.footnoteReferences = footnoteGroups.combined.value.map((footnote) => ({
      type: footnote.type,
      items: footnote.items.sort((a, b) => a.referenceNumber - b.referenceNumber).map((item) => item.referenceNumber),
    }));
  };

  /**
   * Remove provided footnotes from the store
   */
  const removePriceDisplayFootnotes = (footnotes: FootnoteGroup[]) => {
    return removeFootnotes(
      footnotes
        .map((footnote) => footnote.items)
        .flat()
        .map((footnote) => footnote.text),
    );
  };

  /**
   * Anytime footnotes from the footnote store are updated we have to update our own footnote refs
   * but we must check it if update is really needed 'cause we don't want to trigger price display processes each time
   */
  watch(footnotes, (footnotes) => {
    const ppdwcFootnoteUpdate = FootnoteHelper.updateFootnoteReferences(footnoteGroups.ppdwc.value, footnotes);
    const emhFootnoteUpdate = FootnoteHelper.updateFootnoteReferences(footnoteGroups.emh.value, footnotes);

    if (ppdwcFootnoteUpdate.wasUpdated) {
      footnoteGroups.ppdwc.value = ppdwcFootnoteUpdate.updatedFootnoteGroups;
    }

    if (emhFootnoteUpdate.wasUpdated) {
      footnoteGroups.emh.value = emhFootnoteUpdate.updatedFootnoteGroups;
    }

    if (ppdwcFootnoteUpdate.wasUpdated || emhFootnoteUpdate.wasUpdated) {
      assignFootnotesToEl();
    }
  });

  return {
    addEmhFootnote,
    handlePpdwcFootnotes,
    cleanupFootnotes: () => removePriceDisplayFootnotes(footnoteGroups.combined.value),
  };
}

function useOWCPriceDisplay(elementRef: Ref<(HTMLElement & Record<string, unknown>) | undefined>, reference?: string) {
  if (!reference) {
    throw new Error('No footnote reference provided');
  }

  const onReferenceChanged = (footnoteGroups: FootnoteReferenceGroup[]) => {
    if (!elementRef.value) {
      return;
    }

    elementRef.value.footnoteReferences = footnoteGroups;
  };

  const { addGroup, removeGroup, cleanupFootnotes } = useOWCPriceDisplayFootnotes(onReferenceChanged, reference);
  const addEmhFootnote = async (type: BreakdownType, item: string) => {
    await addGroup([{ type, items: [item] }]);
  };

  let ppdwcFootnotes: PriceDisplayEventData['footnotes'] = [];
  const handlePpdwcFootnotes = async ({ detail }: CustomEvent<PriceDisplayEventData>) => {
    try {
      await removeGroup(ppdwcFootnotes as FootnoteGroup[]);
      await addGroup(detail.footnotes as FootnoteGroup[]);
      ppdwcFootnotes = detail.footnotes;
    } catch (error) {
      Logger.error('Error::price-display.composable->handlePpdwcFootnotes()', error);
    }
  };

  return { addEmhFootnote, handlePpdwcFootnotes, cleanupFootnotes };
}

export function usePriceDisplay<T extends Vehicle | ProductDetail>(
  elementRef: Ref<(HTMLElement & Record<string, unknown>) | undefined>,
  product: Ref<T>,
  reference?: string,
) {
  const stockProduct = computed(() =>
    product.value
      ? ({
          productType: ProductApiEnum.STOCK,
          code: getProductDetailParam(product.value, 'code'),
          dcpProductType: product.value.dcpProductType,
          price: product.value.price,
          listPrice: product.value.listPrice,
          badges: getProductDetailParam(product.value, 'badges'),
        } as StockProduct)
      : undefined,
  );

  const priceData = ref<PriceDisplayEventData['priceData']>();
  const totalPrice = computed(
    () =>
      +(priceData.value?.priceBreakDown.find((item) => item.type === PriceType.TotalPrice.toUpperCase())?.amount ?? 0),
  );
  const isInitialized = ref(false);
  const { apiMode, locale } = infuse('config');
  const isPilot = isFootnotePilot();
  const { addEmhFootnote, handlePpdwcFootnotes, cleanupFootnotes } = isPilot
    ? useOWCPriceDisplay(elementRef, reference)
    : useEMHPriceDisplay(elementRef);

  const handlePriceDisplayInit = ({ detail }: CustomEvent<PriceDisplayEventData>) => {
    priceData.value = detail.priceData;
    isInitialized.value = true;
  };
  const handlePriceDisplayUpdate = ({ detail }: CustomEvent<PriceDisplayEventData>) => {
    priceData.value = detail.priceData;
  };

  // If the target element is behind a conditional in the template,
  // the ref might not be resolved yet in a onMounted hook,
  // therefore we use watch.
  watch(
    elementRef,
    (resolvedRef) => {
      if (!resolvedRef) {
        return;
      }

      const env = apiMode.replace(/[0-9]/g, '');

      resolvedRef.environment = env as Environment;
      resolvedRef.configEnvironment = env as Environment;
      resolvedRef.product = unref(stockProduct);
      resolvedRef.locale = locale as SupportedMarket;
      resolvedRef.addEventListener('pdinitialize', handlePriceDisplayInit as unknown as EventListener);
      resolvedRef.addEventListener('pdupdate', handlePriceDisplayUpdate as unknown as EventListener);
    },
    { immediate: true },
  );

  watch(product, () => {
    if (elementRef.value) {
      elementRef.value.product = unref(stockProduct);
    }
  });

  onUnmounted(async () => {
    await cleanupFootnotes();
    elementRef.value?.removeEventListener('pdinitialize', handlePriceDisplayInit as unknown as EventListener);
    elementRef.value?.removeEventListener('pdupdate', handlePriceDisplayUpdate as unknown as EventListener);
  });

  return { addEmhFootnote, handlePpdwcFootnotes, totalPrice, priceData, isInitialized };
}

export default usePriceDisplay;
